/**
 * Chapter029.js — Injury Management & Return to Work (OHSE 029)
 * Migrated from SWMS Platform Chapter029.tsx → JavaScript
 *
 * Props:
 *   data        {object}  injury_management_policy from swmsData
 *   onChange    {fn}      (newPolicy) => void
 *   companyName {string}  from companyInfo.name
 */

import { useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';

const inputCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all';

const COMMITMENTS = [
  'prevent injury and illness by providing a safe and healthy working environment;',
  'participate in the development of an injury management plan and ensure that injury management commences as soon as possible after an employee is injured;',
  'support the injured employee and ensure that early return to work is a normal expectation;',
  'provide suitable duties for an injured employee as soon as possible;',
  'ensure that our injured employees (and anyone representing them) are aware of their rights and responsibilities – including the right to choose their own doctor and rehabilitation provider, and the responsibility to provide accurate information about the injury and its cause;',
  'consult with our employees and, where applicable, unions to ensure that the return-to-work program operates as smoothly as possible;',
  'maintain the confidentiality of injured employee\'s records.',
  'not dismiss an employee as a result of a work-related injury within six months of becoming unfit for employment.',
];

const PROCEDURES = [
  'All injuries must be notified to the supervisor as soon as possible.',
  'All injuries will be recorded in the Register of Injuries.',
  'Our Workers Compensation Scheme Agent will be notified of any injuries that may require compensation within 48 hours.',
  'All injured employees will receive appropriate first aid or medical treatment as soon as possible.',
  'The injured employee must nominate a treating doctor who will be responsible for the medical management of the injury and assist in planning return to work.',
  'A suitable person will be arranged to explain the return to work process to the injured employee.',
  'The injured employee will be offered the assistance of a Work Cover-accredited rehabilitation provider if it becomes evident that they are not likely to resume their pre-injury duties, or cannot do so without changes to the workplace or work practices.',
  'An individual return to work plan will be developed when the injured employee, according to medical advice, can return to work.',
  'The injured employee will be provided with suitable duties that are consistent with medical advice and are meaningful, productive and appropriate to the injured employee\'s physical and psychological condition.',
  'Depending on the individual circumstances of the injured employee, suitable duties may be at the same workplace or a different workplace, the same job with different hours or modified duties, a different job and may involve full-time or part-time hours.',
  'If disagreements about the return to work program or suitable duties arise, the organisation will work with the injured employee and any union representing them to try to resolve the issue.',
  'If all parties are unable to resolve the dispute, the organization will seek to involve the Scheme Agent, an accredited rehabilitation provider, the treating doctor or an injury management consultant.',
];

function emptyProvider() {
  return {
    id: crypto.randomUUID(),
    name: '', organisation: '', contact_details: '', issue_number: '',
  };
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

export default function Chapter029({ data = {}, onChange, companyName }) {
  const cn = companyName || '[Company Name]';

  const defaultPolicy = {
    workplace_contact: { id: crypto.randomUUID(), name: '', organisation: '', contact_details: '', issue_number: '' },
    rehab_providers: [],
    scheme_agent: { id: crypto.randomUUID(), name: '', organisation: '', contact_details: '', issue_number: '' },
  };

  const policy = data && data.workplace_contact ? data : defaultPolicy;

  const updateField = useCallback((field, value) => {
    onChange({ ...policy, [field]: value });
  }, [policy, onChange]);

  const updateWorkplaceContact = useCallback((field, value) => {
    onChange({
      ...policy,
      workplace_contact: { ...policy.workplace_contact, [field]: value },
    });
  }, [policy, onChange]);

  const updateSchemeAgent = useCallback((field, value) => {
    onChange({
      ...policy,
      scheme_agent: { ...policy.scheme_agent, [field]: value },
    });
  }, [policy, onChange]);

  const addProvider = useCallback(() => {
    updateField('rehab_providers', [...(policy.rehab_providers || []), emptyProvider()]);
  }, [policy, updateField]);

  const removeProvider = useCallback((id) => {
    updateField('rehab_providers', (policy.rehab_providers || []).filter(p => p.id !== id));
  }, [policy, updateField]);

  const updateProvider = useCallback((id, field, value) => {
    updateField('rehab_providers', (policy.rehab_providers || []).map(p =>
      p.id === id ? { ...p, [field]: value } : p
    ));
  }, [policy, updateField]);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 029</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">Injury Management &amp; Return to Work</h2>
      </div>

      {/* Declaration */}
      <div className="p-4 bg-[#f8f7f4] rounded-lg border border-[#e0dbd0] text-sm text-[#1a1a2e] leading-relaxed">
        <span className="font-semibold text-[#c9a84c]">{cn}</span>{' '}
        is committed to the return to work of injured employees.
      </div>

      {/* Commitments */}
      <p className="text-sm text-[#1a1a2e] font-medium">As part of this commitment, we will:</p>
      <ul className="text-sm text-[#1a1a2e] leading-relaxed space-y-2 ml-1">
        {COMMITMENTS.map((item, idx) => (
          <li key={idx} className="flex gap-2">
            <span className="text-[#c9a84c] shrink-0">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>

      {/* Procedures */}
      <p className="text-sm text-[#1a1a2e] font-medium">
        To support the above, <span className="font-semibold text-[#c9a84c]">{cn}</span> has established the following procedures.
      </p>
      <ul className="text-sm text-[#1a1a2e] leading-relaxed space-y-2 ml-1">
        {PROCEDURES.map((item, idx) => (
          <li key={idx} className="flex gap-2">
            <span className="text-[#c9a84c] shrink-0">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>

      {/* Workplace Contact */}
      <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest">
        {cn}'s Workplace Contact for the Return-to-Work
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Name">
          <input type="text" value={policy.workplace_contact.name || ''} placeholder="Contact name"
            onChange={e => updateWorkplaceContact('name', e.target.value)} className={inputCls} />
        </Field>
        <Field label="Organisation">
          <input type="text" value={companyName || ''} readOnly
            className={inputCls + ' bg-gray-50 cursor-default'} placeholder="[Company Name]" />
        </Field>
        <Field label="Contact Details">
          <input type="text" value={policy.workplace_contact.contact_details || ''} placeholder="Phone number"
            onChange={e => updateWorkplaceContact('contact_details', e.target.value)} className={inputCls} />
        </Field>
      </div>

      {/* Rehabilitation Providers */}
      <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest">
        {cn}'s Preferred Work Cover-Accredited Rehabilitation Providers
      </p>

      {(!policy.rehab_providers || policy.rehab_providers.length === 0) && (
        <div className="flex flex-col items-center justify-center py-8 text-center border border-[#e0dbd0] rounded-lg bg-white">
          <p className="text-sm text-gray-400 mb-3">No rehabilitation providers listed.</p>
          <button type="button" onClick={addProvider}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-[#1a1a2e] rounded hover:bg-[#252547] transition-colors">
            <Plus className="w-4 h-4" /> Add Provider
          </button>
        </div>
      )}

      {policy.rehab_providers && policy.rehab_providers.length > 0 && (
        <div className="space-y-3">
          {policy.rehab_providers.map((provider) => (
            <div key={provider.id} className="bg-white border border-[#e0dbd0] rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-[#1a1a2e]">
                  {provider.name || 'New Provider'}
                </span>
                <button type="button" onClick={() => removeProvider(provider.id)}
                  className="p-1 text-red-400 hover:text-red-600 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Name">
                  <input type="text" value={provider.name} placeholder="Provider name"
                    onChange={e => updateProvider(provider.id, 'name', e.target.value)} className={inputCls} />
                </Field>
                <Field label="Organisation">
                  <input type="text" value={provider.organisation} placeholder="Organisation"
                    onChange={e => updateProvider(provider.id, 'organisation', e.target.value)} className={inputCls} />
                </Field>
                <Field label="Issue Number">
                  <input type="text" value={provider.issue_number} placeholder="Issue number"
                    onChange={e => updateProvider(provider.id, 'issue_number', e.target.value)} className={inputCls} />
                </Field>
              </div>
            </div>
          ))}
          <div className="flex justify-center">
            <button type="button" onClick={addProvider}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1a1a2e] rounded hover:bg-[rgba(201,168,76,0.15)] border border-[#c9a84c]/40 transition-colors">
              <Plus className="w-4 h-4" /> Add Provider
            </button>
          </div>
        </div>
      )}

      {/* Scheme Agent */}
      <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest">
        {cn}'s Worker's Compensation Scheme Agent
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Name">
          <input type="text" value={policy.scheme_agent.name || ''} placeholder="Agent name"
            onChange={e => updateSchemeAgent('name', e.target.value)} className={inputCls} />
        </Field>
        <Field label="Organisation">
          <input type="text" value={policy.scheme_agent.organisation || ''} placeholder="Organisation"
            onChange={e => updateSchemeAgent('organisation', e.target.value)} className={inputCls} />
        </Field>
        <Field label="Issue Number">
          <input type="text" value={policy.scheme_agent.issue_number || ''} placeholder="Issue number"
            onChange={e => updateSchemeAgent('issue_number', e.target.value)} className={inputCls} />
        </Field>
      </div>
    </div>
  );
}
